import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AnimatedSection from './AnimatedSection';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const procedimientos = [
    { label: t('nav.facialSurgery'), href: '/cirugia-facial' },
    { label: t('nav.bodySurgery'), href: '/cirugia-corporal' },
    { label: t('nav.aestheticMedicine'), href: '/medicina-estetica' },
    { label: t('nav.reconstructiveSurgery'), href: '/cirugia-reparadora' },
    { label: t('common.contact'), href: '/contacto' },
  ];

  const clinica = [
    { label: t('nav.theDoctor'), href: '/el-doctor' },
    // { label: t('nav.certifications'), href: '/el-doctor/acreditaciones' },
    { label: t('common.whereWeAttend'), href: '/donde-atendemos' },
    { label: t('footer.guides'), href: '/guias-recursos' },
    { label: t('footer.siteMap'), href: '/mapa-sitio' },
  ];

  return (
    <footer className="bg-charcoal border-t border-gold/30">
      {/* Main footer content */}
      <div className="container mx-auto px-6 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand column */}
          <AnimatedSection animation="fadeUp" delay={0} className="lg:col-span-1">
            <div className="mb-8">
              <h3 className="font-serif text-2xl text-ivory mb-2">Dr. Ángel Norro</h3>
              <p className="text-xs tracking-[0.2em] uppercase text-text-muted">
                {t('footer.brandSubtitle')}
              </p>
            </div>
            <p className="text-sm text-gold-light/70 leading-relaxed mb-6">
              Centro Sanitario NICA<br />
              Marbella, España<br /><br />
              {t('footer.brandDescription')}
            </p>
            <div className="w-12 h-px bg-gold"></div>
          </AnimatedSection>

          {/* Procedimientos column */}
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <p className="eyebrow text-gold mb-6">{t('footer.specialties')}</p>
            <ul className="space-y-3">
              {procedimientos.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-sm text-gold-light/70 hover:text-gold transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 h-px bg-gold group-hover:w-3 transition-all duration-300"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </AnimatedSection>

          {/* Clínica column */}
          <AnimatedSection animation="fadeUp" delay={0.2}>
            <p className="eyebrow text-gold mb-6">{t('nav.clinic')}</p>
            <ul className="space-y-3 mb-8">
              {clinica.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-sm text-gold-light/70 hover:text-gold transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 h-px bg-gold group-hover:w-3 transition-all duration-300"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </AnimatedSection>

          {/* Contact column */}
          <AnimatedSection animation="fadeUp" delay={0.3}>
            <p className="eyebrow text-gold mb-6">{t('footer.contact')}</p>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:+34952764547" 
                  className="flex items-center gap-3 text-gold-light/70 hover:text-gold transition-colors"
                >
                  <Phone size={16} className="text-gold" />
                  <span className="text-sm">+34 952 76 45 47</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@drangelnorro.com" 
                  className="flex items-center gap-3 text-gold-light/70 hover:text-gold transition-colors"
                >
                  <Mail size={16} className="text-gold" />
                  <span className="text-sm">info@drangelnorro.com</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gold-light/50">
                <MapPin size={16} className="text-gold flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  C/ Carlos Mackintosh, 3<br/>
                  29602 Marbella
                </span>
              </li>
              <li className="text-sm text-gold-light/50 mt-4">
                {t('footer.schedule')}
              </li>
            </ul>
          </AnimatedSection>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-[#111]">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-text-muted">
              © {currentYear} Dr. Ángel Norro Ruiz — {t('footer.rights')}
            </p>
            <div className="flex items-center gap-4 text-xs text-text-muted">
              <span>Nº Colegiado: 38059 / 282905609</span>
              <span className="text-gold/30">|</span>
              <span>Centro Sanitario NICA</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-text-muted">
              <Link to="#" className="hover:text-gold transition-colors">{t('footer.privacy')}</Link>
              <Link to="#" className="hover:text-gold transition-colors">{t('footer.legal')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
